import { useMemo, useState } from 'react';
import { css } from '@emotion/react';
import { DashboardWidget, DashboardWidgetFC, useDashboardWidget } from '@frontend/grid-dashboard';
import { theme } from '@frontend/theme';
import { customROITimePeriods } from '../constants';
import { useHasRoiFeature } from '../hooks/use-has-roi-feature';
import { RoiCards } from '../roi-overview-cards/types';
import { ARWidgetCard } from './ar-widget-card';
import { BTWidgetCard } from './bt-widget-card';
import { CustomTimeHeader } from './custom-time-header';
import { MCTWidgetCard } from './mct-widget-card';

/**
 * @dashboard-widget
 *
 * id: weave-conversion
 * title: Weave Conversion
 * description: Weave Conversion helps you evaluate how the features in Weave are enhancing your practice's performance.
 * icon: dollar-sign-small
 */

export const WeaveConversionWidget: DashboardWidgetFC = () => {
  const timePeriod = customROITimePeriods.lastQuarter;
  const { isEditMode } = useDashboardWidget();
  const { isFeatureEnabled: isBulkTextingRoiEnabled } = useHasRoiFeature({ roi: 'bulk-texting' });
  const [visibleCards, setVisibleCards] = useState<RoiCards>({});

  const visibleCardsCount = useMemo(() => {
    return Object.values(visibleCards).filter(Boolean).length;
  }, [visibleCards]);

  const commonProps = {
    timePeriod,
    handleUpdateActiveCards: (card: keyof RoiCards, value?: boolean) => {
      setVisibleCards((prev) => ({
        ...prev,
        [card]: value,
      }));
    },
    showTrendLine: visibleCardsCount < 3,
  };

  return (
    <DashboardWidget>
      <DashboardWidget.Header>
        <CustomTimeHeader label={timePeriod.label} />
      </DashboardWidget.Header>
      <DashboardWidget.Content css={styles.content(isEditMode, visibleCardsCount > 2)}>
        <MCTWidgetCard {...commonProps} />
        {isBulkTextingRoiEnabled && <BTWidgetCard {...commonProps} />}
        <ARWidgetCard {...commonProps} />
      </DashboardWidget.Content>
    </DashboardWidget>
  );
};

WeaveConversionWidget.config = {
  size: 'medium-narrow',
  feature: 'analytics',
};

const styles = {
  content: (isEditMode?: boolean, smallCards?: boolean) => css`
    display: flex;
    flex-direction: ${smallCards ? 'row' : 'column'};
    flex-wrap: wrap;
    gap: ${theme.spacing(1)};
    margin-top: ${theme.spacing(0.75)};
    pointer-events: ${isEditMode ? 'none' : 'auto'};

    button {
      width: ${smallCards ? `calc(50% - ${theme.spacing(0.5)})` : '100%'};
    }
  `,
};
